/**
 * This file serves to maintain compatibility with the old player initialization
 * logic from the CMS. The vast majority of this was copied and pasted directly
 * from @dreager's `__init__.js` and `utilities/Setup.js` scripts
 */
import {version} from "../package.json";
import videojs from "video.js";
import { parseUrl } from "../utilities/Url";
import Cache from "../utilities/Cache";

const logger = videojs.createLogger("Setup");

// TODO: This isn't security
// just to make it harder for bots scraping urls
const adminHostnames = [
	// btoa("blueframe-player-flash.s3.amazonaws.com")
	"Ymx1ZWZyYW1lLXBsYXllci1mbGFzaC5zMy5hbWF6b25hd3MuY29t",
	// btoa("localhost")
	"bG9jYWxob3N0",
	// btoa("test.local")
	"dGVzdC5sb2NhbA==",
	// btoa("192.168.2.126") office ip
	"MTkyLjE2OC4yLjEyNg=="
];
// blocks/allows admin config options
let privileged = adminHostnames.indexOf(btoa(window.location.hostname)) >= 0;

// Will be set later
var config;

const adminConfig = {
	no_ads: true,
	no_network_id: true,
	no_analytics: true
};

var setup = (function ()
{
	// Not very efficient, but fixes bools and numbers being strings
	function parseJsonObject(object)
	{
		return JSON.parse(JSON.stringify(object), function (key, value)
		{
			if (typeof value === "string")
			{
				try
				{
					return JSON.parse(value);
				}
				catch (e)
				{
					return value;
				}
			}
			else
			{
				return value;
			}
		});
	}

	// Combines url parameters, player_config and defaults_config into one object
	function setupConfig()
	{
		var params = parseUrl(window.location).parameters;

		const playerConfig = window.player_config || {};

		// Some options like allow_test are found in player_config.settings and
		// not directly in player_config. This is a really stupid and simple way
		// to make less work for myself
		config = parseJsonObject(videojs.mergeOptions({}, playerConfig, playerConfig.settings, params));
		config.features = config.features || {};
		config.limit = config.limit || {};

		// An encoded url will break things
		config.vmap_url = decodeURIComponent(config.vmap_url);
		config.logging = config.logging ? typeof config.logging === "string" ? config.logging : "info" : "silent";
		let playerDomain = config.playerDomain;
		try
		{
			if (window.self === window.top || playerDomain === "")
				playerDomain = window.location.href;
		}
		catch (e) { /* do nothing */ }
		config.domain = playerDomain;

		const vmapDetails = parseUrl(config.vmap_url);
		config.vmap_domain = vmapDetails.hostname;
		config.broadcast_id = vmapDetails.pathname.split("/").pop();

		// TODO: This is a real bad way to detect admin player
		const admin = config.domain.indexOf("/admin/") >= 0;
		privileged = privileged || admin;
		if (!privileged)
		{
			// Disable these features for non-privileged users
			for (var key in adminConfig)
				if (Object.prototype.hasOwnProperty.call(adminConfig, key))
					config[key] = false;
		}

		// Admin players should have full logging
		config.logging = admin ? "trace" : config.logging;

		// Default
		config.autohide_controls = config.autohide_controls || true;

		// Thumbnails are not support with content override, for obvious reasons
		config.features.thumbnails = !config.content_override_url;

		// Until we add proper support to the CMS (to put this in the
		// player_config), we can read the window parameters manually
		if (typeof config.no_automute === "undefined")
		{
			config.no_automute = window.location.href.indexOf("automute=0") >= 0;
		}

		if (config.caching === false)
		{
			Cache.disable();
		}
	}

	// Used to do a lot more. Now just logs the player version and config
	function setupLogging()
	{
		logger(`BluePlayer v${version}`);
		if (privileged) logger("\tAdmin mode active");

		logger("\tBroadcast: " + config.broadcast_id);
		logger("Config: ", JSON.stringify(config));
	}

	return function ()
	{
		setupConfig();
		setupLogging();
	};
})();

function initHTML5Player()
{
	// TODO: We may need to load our own CSS in case the appropriate CSS file
	// TODO: was not included on the page. Honestly, though, if they're
	// TODO: including volarplayer.min.js, I don't think it's a stretch to
	// TODO: expect them to include volarplayer.min.css

	// Prevent scroll bars on the body
	document.body.style.overflow = "hidden";

	// Removes elements added by embed.php and player-setup.js
	document.body.innerHTML =
		`<video
			id="video_container"
			class="video-js"
			${config.auto_play ? "autoplay" : ""}
			${config.poster ? "poster=\"" + config.poster + "\"" : ""}
			${config.volume ? "volume=\"" + config.volume + "\"" : ""}
			style="width:100%; height:100%; position:relative;"
		></video>`;

	// Initializes our player
	const app = window.bfplayer("video_container", {
		// The default "inactivityTimeout" is 2000ms
		// Setting it to 0 disables inactivity (control bar won't hide)
		inactivityTimeout: config.autohide_controls ? 2 * 1000 : 0,
		/* eslint-disable-next-line no-magic-numbers */
		playbackRates: [0.25, 0.5, 1.0, 1.5, 2.0, 4.0],
	});

	// If videojs-contrib-ads is available, it MUST be loaded first
	// This is because videojs-contrib-ads overrides a lot of player events
	if (Object.prototype.hasOwnProperty.call(videojs.getPlugins(), "ads"))
	{
		app.ads();
	}

	// Finally, pass any configuration options to plugins that need to be aware
	// of them. We place this here instead of in the VideoJS options so that if
	// a plugin is disabled or removed it won't break player initialization
	const pluginOptions = {
		blueframeAds: {
			enabled: !config.no_ads,
			descriptionUrl: config.domain
		},
		liveDVR: {
			enabled: typeof config.dvr === "undefined" ? true : config.dvr,
			temporalDelay: config.default_temporal_delay
		},
		volarVMAP: {
			contentOverride: config.content_override_url
		},
		adTriggerVMAP: {
			enabled: !config.no_ads,
			vmapOverride: config.vmap_override_url
		},
		adTriggerMetadata: {
			enabled: !config.no_ads,
			vmapOverride: config.vmap_override_url
		},
		behaviorChangeAutoplayFix: {
			enabled: !config.no_automute
		},
		// eslint-disable-next-line id-length
		behaviorChangeLimitConcurrentViewers: {
			serviceUrl: config.limit.service_url,
			heartbeatInterval: config.limit.heartbeat_interval,
			timeout: config.limit.timeout,
			orderNumber: config.limit.order_number,
			concurrentViewers: config.limit.concurrent_viewers,
			signature: config.limit.signature
		},
		controlBarButtonBlueFrameClipping: {
			enabled: privileged
		},
		controlBarButtonPopupWindow: {
			force: config.popup_window_button
		},
		infoMenuOptionReleaseNotes: {
			enable: privileged
		},
		infoMenuOptionStatsForNerds: {
			open: config.show_stats
		},
		vmapTagParserNetworkID: {
			enabled: !config.no_network_id
		},
		vmapTagParserNotifiers: {
			allow_test: config.allow_test,
			start_override: config.start_time,
			overrideStatus: config.status,
			messages: {
				upcoming: config.not_streaming_message
			}
		},
		vmapTagParserPreviewSets: {
			enabled: config.features.thumbnails
		},
		vmapTagParserStats: {
			enabled: !config.no_analytics,
			domain: config.domain,
			purchaseOrderNumber: config.limit.order_number
		}
	};

	Object.keys(videojs.getPlugins()).forEach(plugin =>
	{
		if (typeof app[plugin] === "function")
		{
			app[plugin](pluginOptions[plugin] || {});
		}
	});

	// Sets the player source to our VMAP URL
	app.src({
		src: config.vmap_url,
		type: "video/x.volar-vmap"
	});

	// Make logs more easily accessible programmatically
	app.getLogs = videojs.log.history;

	return app;
}

function initPlayer()
{
	setup();

	return initHTML5Player();
}

window.$v = function ()
{
	const player = initPlayer();

	// For backwards compatibility with player-setup.js
	// TODO: Probably make a plugin so addEventListener can be an alias for on,
	// TODO: because that's pretty standard stuff
	// Such a plugin would have to come after v5.0.0 (and the deprecation of
	// player-setup.js) because player-setup.js passes a string, not a function.
	// Alternatively we could build it to accept strings (converting them to
	// functions) and deprecate that usage - but that's ugly.
	player.addEventListener = function() { /* do nothing for now */ };

	// This kills the player-setup
	window.$v = function () { return player; };

	return player;
};
